import { useEffect, useState } from "react";

import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useAuth from "@/contexts/auth-context";
import { allPlansExpired } from "@/helpers/customer-sales-product-helpers";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import IUser from "@/interfaces/user.interface";
import { UserProfile } from "@/types/user.types";

import useGetAllCustomerSalesProducts from "./requests/customer-sales-product/use-get-all-customer-sales-product";

const checkPermissions = (
  requiredProfiles: UserProfile[],
  user: IUser | null,
  customerSalesProducts: ICustomerSalesProduct[]
) => {
  try {
    // Se não há dados de produto ainda, não bloqueie o fluxo de permissão por perfil.
    // O legado não impede autenticação por falha/ausência ao carregar planos.
    if (!customerSalesProducts) {
      // Avalia apenas por perfil
      const hasNoProfile = !requiredProfiles.some((profile) =>
        user?.userProfile?.some((userProfile) => userProfile.profileId === profile)
      );
      return hasNoProfile;
    }

    // Se há lista mas está vazia, seguimos o legado: sem produtos contratados → nega.
    if (customerSalesProducts.length === 0) {
      return true;
    }

    // Se todos os planos estão expirados, nega
    if (allPlansExpired(customerSalesProducts)) {
      return true;
    }

    // Se usuário não carregado ainda, considere sem permissão até carregar
    if (!user) {
      return true;
    }

    // Caso usuário sem perfis porém com planos ativos, permita (compatível com observação do legado)
    if (!user.userProfile || user.userProfile.length === 0) {
      return false;
    }

    // Verifica perfil requerido
    const hasNoPermission = !requiredProfiles.some((profile) =>
      user?.userProfile.some((userProfile) => userProfile.profileId === profile)
    );

    return hasNoPermission;
  } catch (_e) {
    // Em caso de erro inesperado, preferimos não travar
    const hasNoProfile = !requiredProfiles.some((profile) =>
      user?.userProfile?.some((userProfile) => userProfile.profileId === profile)
    );
    return hasNoProfile;
  }
};

const useHasNoPermission = (requiredProfiles: UserProfile[]) => {
  const { user, isAuthReady } = useAuth();
  const [hasNoPermission, setHasNoPermission] = useState(true);
  // Só busca planos após auth pronto; atendentes/representantes verão gating por perfil mesmo sem planos
  const allCustomerSalesProducts = useGetAllCustomerSalesProducts({
    pageNumber,
    pageSize,
    enabled: isAuthReady,
  });
  const { data: customerSalesProduct, isLoading } = allCustomerSalesProducts;

  useEffect(() => {
    // Aguarda auth pronto e carregamento de produtos antes de determinar permissões
    if (!isAuthReady || isLoading) return;

    const customerSalesProducts = customerSalesProduct?.items || [];
    const noPermission = checkPermissions(requiredProfiles, user, customerSalesProducts);

    setHasNoPermission(noPermission);
  }, [requiredProfiles, user, customerSalesProduct, isLoading, isAuthReady]);

  return hasNoPermission;
};

export default useHasNoPermission;
